import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  api,
  Client,
  Insight,
  RoadmapItem,
  LoopViewData,
  AdoptionStatus,
  NotificationChannel,
  RoadmapModule,
  RoadmapWorkType,
  RoadmapHorizon,
  RoadmapStatus,
} from '../lib/api';

const ADOPTION_OPTIONS: { value: AdoptionStatus; label: string }[] = [
  { value: 'aware', label: 'Aware' },
  { value: 'evaluating', label: 'Evaluating' },
  { value: 'adopted', label: 'Adopted' },
  { value: 'declined', label: 'Declined' },
  { value: 'not_applicable', label: 'N/A' },
];

const CHANNEL_OPTIONS: { value: NotificationChannel; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'slack', label: 'Slack' },
  { value: 'other', label: 'Other' },
];

export function LoopView() {
  const { roadmapItemId } = useParams<{ roadmapItemId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loopData, setLoopData] = useState<LoopViewData | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<RoadmapItem>>({});

  // Modals
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showShipModal, setShowShipModal] = useState(false);

  // Search insights for linking
  const [searchResults, setSearchResults] = useState<
    Array<Insight & { client_name: string }>
  >([]);
  const [searchFilters, setSearchFilters] = useState({
    client_id: '',
    search_text: '',
  });

  // Notification form
  const [notifyForm, setNotifyForm] = useState({
    client_id: '',
    notified_at: new Date().toISOString().split('T')[0],
    channel: 'email' as NotificationChannel,
    notes: '',
  });

  // Add client form
  const [addClientForm, setAddClientForm] = useState({
    client_id: '',
    notes: '',
  });

  // Ship form
  const [shipDate, setShipDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  useEffect(() => {
    loadData();
    loadClients();
  }, [roadmapItemId]);

  const loadData = async () => {
    if (!roadmapItemId) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.getLoopView(roadmapItemId);
      setLoopData(data);
      setEditForm(data.roadmap_item);
    } catch (err) {
      setError('Failed to load loop data');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const clientList = await api.listClients();
      setClients(clientList);
    } catch {
      // Silent fail
    }
  };

  const searchInsights = async () => {
    if (!roadmapItemId) return;
    try {
      const results = await api.searchInsightsForLinking(roadmapItemId, {
        ...searchFilters,
        client_id: searchFilters.client_id || undefined,
        search_text: searchFilters.search_text || undefined,
        limit: 20,
      });
      setSearchResults(results);
    } catch {
      setError('Failed to search insights');
    }
  };

  const handleLinkInsight = async (insightId: string) => {
    if (!roadmapItemId) return;
    try {
      await api.linkInsightToRoadmap(roadmapItemId, insightId);
      loadData();
      searchInsights();
    } catch {
      setError('Failed to link insight');
    }
  };

  const handleUnlinkInsight = async (insightId: string) => {
    if (!roadmapItemId) return;
    try {
      await api.unlinkInsightFromRoadmap(roadmapItemId, insightId);
      loadData();
    } catch {
      setError('Failed to unlink insight');
    }
  };

  const handleAddNotification = async () => {
    if (!roadmapItemId) return;
    try {
      await api.addClientNotification(roadmapItemId, {
        client_id: notifyForm.client_id,
        notified_at: notifyForm.notified_at,
        channel: notifyForm.channel,
        notes: notifyForm.notes || undefined,
      });
      setShowNotifyModal(false);
      setNotifyForm({
        client_id: '',
        notified_at: new Date().toISOString().split('T')[0],
        channel: 'email',
        notes: '',
      });
      loadData();
    } catch {
      setError('Failed to add notification');
    }
  };

  const handleUpdateAdoption = async (
    clientId: string,
    adoptionStatus: AdoptionStatus
  ) => {
    if (!roadmapItemId) return;
    try {
      await api.updateClientAdoption(roadmapItemId, {
        client_id: clientId,
        adoption_status: adoptionStatus,
      });
      loadData();
    } catch {
      setError('Failed to update adoption status');
    }
  };

  const handleAddClient = async () => {
    if (!roadmapItemId) return;
    try {
      await api.addClientToLoop(roadmapItemId, {
        client_id: addClientForm.client_id,
        notes: addClientForm.notes || undefined,
      });
      setShowAddClientModal(false);
      setAddClientForm({ client_id: '', notes: '' });
      loadData();
    } catch {
      setError('Failed to add client to loop');
    }
  };

  const handleMarkShipped = async () => {
    if (!roadmapItemId) return;
    try {
      await api.markRoadmapItemShipped(roadmapItemId, shipDate);
      setShowShipModal(false);
      loadData();
    } catch {
      setError('Failed to mark as shipped');
    }
  };

  const handleSaveEdit = async () => {
    if (!roadmapItemId) return;
    try {
      // Convert null values to undefined for the API
      const updateData: Parameters<typeof api.updateRoadmapItem>[1] = {};
      if (editForm.title) updateData.title = editForm.title;
      if (editForm.description !== undefined) updateData.description = editForm.description || undefined;
      if (editForm.module_touched) updateData.module_touched = editForm.module_touched;
      if (editForm.work_type) updateData.work_type = editForm.work_type;
      if (editForm.horizon) updateData.horizon = editForm.horizon;
      if (editForm.status) updateData.status = editForm.status;
      if (editForm.target_ship_date !== undefined) updateData.target_ship_date = editForm.target_ship_date || undefined;
      if (editForm.owner !== undefined) updateData.owner = editForm.owner || undefined;
      if (editForm.notes !== undefined) updateData.notes = editForm.notes || undefined;

      await api.updateRoadmapItem(roadmapItemId, updateData);
      setIsEditing(false);
      loadData();
    } catch {
      setError('Failed to update roadmap item');
    }
  };

  const handleDelete = async () => {
    if (!roadmapItemId) return;
    if (!confirm('Are you sure you want to delete this roadmap item?')) return;
    try {
      await api.deleteRoadmapItem(roadmapItemId);
      navigate('/roadmap');
    } catch {
      setError('Failed to delete roadmap item');
    }
  };

  const formatLabel = (value: string) =>
    value
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

  if (loading) return <div className="loading">Loading...</div>;
  if (!loopData) return <div className="alert alert-error">Not found</div>;

  const { roadmap_item, linked_insights, client_loop } = loopData;

  // Get clients not yet in the loop for adding
  const clientsNotInLoop = clients.filter(
    (c) => !client_loop.some((cl) => cl.client_id === c.client_id)
  );

  return (
    <div>
      <div className="page-header">
        <div className="flex justify-between items-center">
          <div>
            <button
              className="btn btn-ghost btn-small"
              onClick={() => navigate('/roadmap')}
              style={{ marginBottom: '0.5rem' }}
            >
              &larr; Back to Roadmap
            </button>
            <h1 className="page-title">{roadmap_item.title}</h1>
            <p className="page-subtitle">
              Loop View - Track insights, notifications, and adoption
            </p>
          </div>
          <div className="btn-group">
            {roadmap_item.status !== 'shipped' && (
              <button
                className="btn btn-primary"
                onClick={() => setShowShipModal(true)}
              >
                Mark Shipped
              </button>
            )}
            <button
              className="btn btn-secondary"
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? 'Cancel' : 'Edit'}
            </button>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Roadmap Item Details */}
      <div className="card mb-1">
        <div className="card-header">
          <h3>Roadmap Item Details</h3>
        </div>
        <div className="card-body">
          {isEditing ? (
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className="form-input"
                  value={editForm.title || ''}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, title: e.target.value }))
                  }
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-textarea"
                  rows={2}
                  value={editForm.description || ''}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, description: e.target.value }))
                  }
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Module</label>
                  <select
                    className="form-select"
                    value={editForm.module_touched || ''}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        module_touched: e.target.value as RoadmapModule,
                      }))
                    }
                  >
                    <option value="navigator">Navigator</option>
                    <option value="studio">Studio</option>
                    <option value="quant_engine">Quant Engine</option>
                    <option value="dashboard">Dashboard</option>
                    <option value="cross_cutting">Cross-Cutting</option>
                    <option value="platform">Platform</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Work Type</label>
                  <select
                    className="form-select"
                    value={editForm.work_type || ''}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        work_type: e.target.value as RoadmapWorkType,
                      }))
                    }
                  >
                    <option value="r_and_d">R&D</option>
                    <option value="engineering">Engineering</option>
                    <option value="product_ux">Product/UX</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Horizon</label>
                  <select
                    className="form-select"
                    value={editForm.horizon || ''}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        horizon: e.target.value as RoadmapHorizon,
                      }))
                    }
                  >
                    <option value="near_term">Near-Term</option>
                    <option value="mid_term">Mid-Term</option>
                    <option value="long_term">Long-Term</option>
                    <option value="shipped">Shipped</option>
                    <option value="deprioritized">Deprioritized</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={editForm.status || ''}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        status: e.target.value as RoadmapStatus,
                      }))
                    }
                  >
                    <option value="proposed">Proposed</option>
                    <option value="active">Active</option>
                    <option value="shipped">Shipped</option>
                    <option value="deprioritized">Deprioritized</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Target Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={editForm.target_ship_date || ''}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        target_ship_date: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Owner</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editForm.owner || ''}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, owner: e.target.value }))
                    }
                  />
                </div>
              </div>
              <div className="flex gap-1 mt-1">
                <button className="btn btn-primary" onClick={handleSaveEdit}>
                  Save
                </button>
                <button
                  className="btn btn-error"
                  onClick={handleDelete}
                  style={{ marginLeft: 'auto' }}
                >
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <div className="detail-grid">
              <div>
                <span className="text-muted">Module:</span>{' '}
                <span className="tag tag-capability">
                  {formatLabel(roadmap_item.module_touched)}
                </span>
              </div>
              <div>
                <span className="text-muted">Work Type:</span>{' '}
                {formatLabel(roadmap_item.work_type)}
              </div>
              <div>
                <span className="text-muted">Horizon:</span>{' '}
                {formatLabel(roadmap_item.horizon)}
              </div>
              <div>
                <span className="text-muted">Status:</span>{' '}
                <span
                  className={`tag ${roadmap_item.status === 'shipped' ? 'tag-valid' : 'tag-warning'}`}
                >
                  {formatLabel(roadmap_item.status)}
                </span>
              </div>
              <div>
                <span className="text-muted">Target:</span>{' '}
                {roadmap_item.target_ship_date || '-'}
              </div>
              <div>
                <span className="text-muted">Shipped:</span>{' '}
                {roadmap_item.actual_ship_date || '-'}
              </div>
              <div>
                <span className="text-muted">Owner:</span>{' '}
                {roadmap_item.owner || '-'}
              </div>
              {roadmap_item.description && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <span className="text-muted">Description:</span>{' '}
                  {roadmap_item.description}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Linked Insights */}
      <div className="card mb-1">
        <div className="card-header flex justify-between items-center">
          <h3>Linked Insights ({linked_insights.length})</h3>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => {
              setShowLinkModal(true);
              searchInsights();
            }}
          >
            + Link Insight
          </button>
        </div>
        <div className="card-body">
          {linked_insights.length === 0 ? (
            <p className="text-muted">No insights linked yet</p>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Insight</th>
                    <th>Client</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {linked_insights.map((insight) => (
                    <tr key={insight.insight_id}>
                      <td>
                        <div className="insight-text">
                          {insight.insight_text}
                        </div>
                        <div className="quote" style={{ marginTop: '0.25rem' }}>
                          {insight.supporting_quote}
                        </div>
                      </td>
                      <td>{insight.client_name}</td>
                      <td>{insight.meeting_date}</td>
                      <td>
                        <button
                          className="btn btn-ghost btn-small"
                          onClick={() =>
                            handleUnlinkInsight(insight.insight_id)
                          }
                        >
                          Unlink
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Client Loop */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3>Client Loop ({client_loop.length})</h3>
          <div className="btn-group">
            <button
              className="btn btn-secondary btn-small"
              onClick={() => setShowNotifyModal(true)}
            >
              + Add Notification
            </button>
            <button
              className="btn btn-secondary btn-small"
              onClick={() => setShowAddClientModal(true)}
            >
              + Add Client
            </button>
          </div>
        </div>
        <div className="card-body">
          {client_loop.length === 0 ? (
            <p className="text-muted">No clients in loop yet</p>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Insights</th>
                    <th>Last Notified</th>
                    <th>Adoption Status</th>
                  </tr>
                </thead>
                <tbody>
                  {client_loop.map((cl) => (
                    <tr key={cl.client_id}>
                      <td>{cl.client_name}</td>
                      <td>
                        <span
                          className={
                            cl.insight_count > 0
                              ? 'tag tag-valid'
                              : 'text-muted'
                          }
                        >
                          {cl.insight_count}
                        </span>
                      </td>
                      <td>
                        {cl.last_notification ? (
                          <div>
                            <div>{cl.last_notification.notified_at}</div>
                            <div className="text-small text-muted">
                              via {cl.last_notification.channel}
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted">Not notified</span>
                        )}
                      </td>
                      <td>
                        <select
                          className="form-select form-select-small"
                          value={cl.adoption?.adoption_status || 'aware'}
                          onChange={(e) =>
                            handleUpdateAdoption(
                              cl.client_id,
                              e.target.value as AdoptionStatus
                            )
                          }
                        >
                          {ADOPTION_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Link Insight Modal */}
      {showLinkModal && (
        <div className="modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div
            className="modal modal-large"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Link Insight</h2>
              <button
                className="modal-close"
                onClick={() => setShowLinkModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="flex gap-1 mb-1">
                <select
                  className="form-select"
                  value={searchFilters.client_id}
                  onChange={(e) =>
                    setSearchFilters((p) => ({
                      ...p,
                      client_id: e.target.value,
                    }))
                  }
                >
                  <option value="">All Clients</option>
                  {clients.map((c) => (
                    <option key={c.client_id} value={c.client_id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search text..."
                  value={searchFilters.search_text}
                  onChange={(e) =>
                    setSearchFilters((p) => ({
                      ...p,
                      search_text: e.target.value,
                    }))
                  }
                />
                <button className="btn btn-primary" onClick={searchInsights}>
                  Search
                </button>
              </div>

              {searchResults.length === 0 ? (
                <p className="text-muted">No insights found</p>
              ) : (
                <div className="table-container" style={{ maxHeight: '400px' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Insight</th>
                        <th>Client</th>
                        <th>Date</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((insight) => (
                        <tr key={insight.insight_id}>
                          <td>
                            <div className="insight-text">
                              {insight.insight_text}
                            </div>
                          </td>
                          <td>{insight.client_name}</td>
                          <td>{insight.meeting_date}</td>
                          <td>
                            <button
                              className="btn btn-primary btn-small"
                              onClick={() =>
                                handleLinkInsight(insight.insight_id)
                              }
                            >
                              Link
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Notification Modal */}
      {showNotifyModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowNotifyModal(false)}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Notification</h2>
              <button
                className="modal-close"
                onClick={() => setShowNotifyModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Client *</label>
                <select
                  className="form-select"
                  value={notifyForm.client_id}
                  onChange={(e) =>
                    setNotifyForm((p) => ({ ...p, client_id: e.target.value }))
                  }
                >
                  <option value="">Select client...</option>
                  {clients.map((c) => (
                    <option key={c.client_id} value={c.client_id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={notifyForm.notified_at}
                    onChange={(e) =>
                      setNotifyForm((p) => ({
                        ...p,
                        notified_at: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Channel</label>
                  <select
                    className="form-select"
                    value={notifyForm.channel}
                    onChange={(e) =>
                      setNotifyForm((p) => ({
                        ...p,
                        channel: e.target.value as NotificationChannel,
                      }))
                    }
                  >
                    {CHANNEL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-textarea"
                  rows={2}
                  value={notifyForm.notes}
                  onChange={(e) =>
                    setNotifyForm((p) => ({ ...p, notes: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowNotifyModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddNotification}
                disabled={!notifyForm.client_id}
              >
                Add Notification
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Client Modal */}
      {showAddClientModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowAddClientModal(false)}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Client to Loop</h2>
              <button
                className="modal-close"
                onClick={() => setShowAddClientModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Client *</label>
                <select
                  className="form-select"
                  value={addClientForm.client_id}
                  onChange={(e) =>
                    setAddClientForm((p) => ({
                      ...p,
                      client_id: e.target.value,
                    }))
                  }
                >
                  <option value="">Select client...</option>
                  {clientsNotInLoop.map((c) => (
                    <option key={c.client_id} value={c.client_id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-textarea"
                  rows={2}
                  value={addClientForm.notes}
                  onChange={(e) =>
                    setAddClientForm((p) => ({ ...p, notes: e.target.value }))
                  }
                  placeholder="Why is this client interested?"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddClientModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddClient}
                disabled={!addClientForm.client_id}
              >
                Add Client
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Shipped Modal */}
      {showShipModal && (
        <div className="modal-overlay" onClick={() => setShowShipModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Mark as Shipped</h2>
              <button
                className="modal-close"
                onClick={() => setShowShipModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Ship Date *</label>
                <input
                  type="date"
                  className="form-input"
                  value={shipDate}
                  onChange={(e) => setShipDate(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowShipModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleMarkShipped}
                disabled={!shipDate}
              >
                Mark Shipped
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
